import React, { useState, useContext, useMemo } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  Brush,
  ComposedChart,
  Bar
} from 'recharts';
import './ScoreGraph.css';

/**
 * ScoreGraph component displays a graph of score distributions
 * 
 * @param {Object} props - Component props
 * @param {Array} props.players - Array of player objects with scores
 * @param {string} props.currentUsername - Username of the current user
 * @param {Array} props.matchingPlayers - Optional array of players from a matching tournament
 * @param {string} props.gameTitle - Title of the game
 */
function ScoreGraph({ players, currentUsername, matchingPlayers, gameTitle }) {
  const { theme } = useContext(ThemeContext);
  const [graphType, setGraphType] = useState('area');
  
  // Calculate histogram data
  const histogramData = useMemo(() => {
    if (!players || players.length === 0) return [];
    
    // Get all scores
    const scores = players.map(player => player.score);
    
    // Find min and max scores
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    
    // Calculate bin size (we'll use 10 bins)
    const range = maxScore - minScore;
    const binSize = Math.max(1, Math.ceil(range / 10));
    
    // Create bins
    const bins = {};
    for (let i = minScore; i <= maxScore; i += binSize) {
      bins[i] = { 
        binStart: i, 
        binEnd: i + binSize - 1,
        count: 0,
        label: `${i.toLocaleString()}-${(i + binSize - 1).toLocaleString()}`
      };
    }
    
    // Count scores in each bin
    scores.forEach(score => {
      const binStart = Math.floor((score - minScore) / binSize) * binSize + minScore;
      if (bins[binStart]) {
        bins[binStart].count++;
      }
    });
    
    // Convert to array for recharts
    return Object.values(bins);
  }, [players]);
  
  // Prepare area chart data
  const areaChartData = useMemo(() => {
    if (!players || players.length === 0) return [];
    
    // Sort players by score
    const sortedPlayers = [...players].sort((a, b) => a.score - b.score);
    
    // Create data points
    return sortedPlayers.map((player, index) => ({
      name: player.userName,
      score: player.score,
      rank: player.rank,
      isCurrentUser: player.userName.toLowerCase() === currentUsername?.toLowerCase(),
      // If we have matching players, include that data
      matchingScore: matchingPlayers?.find(p => p.userName === player.userName)?.score
    }));
  }, [players, matchingPlayers, currentUsername]);
  
  // Find the current user's data point
  const currentUserData = useMemo(() => {
    if (!currentUsername) return null;
    return players.find(player => player.userName.toLowerCase() === currentUsername.toLowerCase());
  }, [players, currentUsername]);
  
  // Custom tooltip for the area chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`score-graph-tooltip ${theme}`}>
          <p className="player-name">{label}</p>
          <p className="score">Score: {payload[0].value.toLocaleString()}</p>
          <p className="rank">Rank: #{payload[0].payload.rank}</p>
          {payload.length > 1 && payload[1].value && (
            <p className="matching-score">Matching Score: {payload[1].value.toLocaleString()}</p>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Custom tooltip for the histogram
  const HistogramTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className={`score-graph-tooltip ${theme}`}>
          <p>Score Range: {data.label}</p>
          <p>Number of Players: {data.count}</p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className={`score-graph-container ${theme}`}>
      <div className="score-graph-controls">
        <button 
          className={`graph-type-button ${graphType === 'histogram' ? 'active' : ''} ${theme}`}
          onClick={() => setGraphType('histogram')}
        >
          Histogram
        </button>
        <button 
          className={`graph-type-button ${graphType === 'area' ? 'active' : ''} ${theme}`}
          onClick={() => setGraphType('area')}
        >
          Area Chart
        </button>
      </div>
      
      <div className="score-graph-wrapper">
        {graphType === 'histogram' ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={histogramData}
              margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="label" 
                label={{ value: 'Score Range', position: 'insideBottom', offset: -10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                label={{ value: 'Number of Players', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<HistogramTooltip />} />
              <Legend />
              <Bar 
                dataKey="count" 
                name="Players" 
                fill={theme === 'dark' ? '#4CAF50' : '#82ca9d'} 
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={areaChartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
              isAnimationActive={true}
              animationDuration={500}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={false}
                label={{ value: 'Players (Sorted by Score)', position: 'insideBottom', offset: -10 }}
              />
              <YAxis 
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="score" 
                name="Current Scores" 
                stroke={theme === 'dark' ? '#4CAF50' : '#82ca9d'} 
                fill={theme === 'dark' ? '#4CAF50' : '#82ca9d'} 
                fillOpacity={0.6}
                isAnimationActive={true}
                animationDuration={500}
                activeDot={(props) => {
                  const { payload } = props;
                  return payload.isCurrentUser ? (
                    <svg>
                      <circle 
                        cx={props.cx} 
                        cy={props.cy} 
                        r={8} 
                        fill="#ff5722" 
                        stroke="#000000"
                        strokeWidth={2}
                      />
                    </svg>
                  ) : (
                    <svg>
                      <circle 
                        cx={props.cx} 
                        cy={props.cy} 
                        r={4} 
                        fill={theme === 'dark' ? '#4CAF50' : '#82ca9d'} 
                      />
                    </svg>
                  );
                }}
                dot={(props) => {
                  const { payload } = props;
                  if (payload.isCurrentUser) {
                    return (
                      <svg>
                        <circle 
                          cx={props.cx} 
                          cy={props.cy} 
                          r={6} 
                          fill="#ff5722" 
                          stroke="#000000"
                          strokeWidth={2}
                        />
                      </svg>
                    );
                  }
                  return null; // Only show dot for current user
                }}
              />
              {matchingPlayers && (
                <Area 
                  type="monotone" 
                  dataKey="matchingScore" 
                  name="Matching Tournament Scores" 
                  stroke={theme === 'dark' ? '#2196F3' : '#8884d8'} 
                  fill={theme === 'dark' ? '#2196F3' : '#8884d8'} 
                  fillOpacity={0.6}
                  isAnimationActive={true}
                  animationDuration={500}
                />
              )}
              <Brush dataKey="name" height={30} stroke="#8884d8" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      
      {currentUserData && (
        <div className={`current-user-stats ${theme}`}>
          <p>
            Your score: <strong>{currentUserData.score.toLocaleString()}</strong> (Rank: #{currentUserData.rank})
          </p>
          {matchingPlayers && currentUsername && (
            <p>
              {(() => {
                const matchingUserData = matchingPlayers.find(
                  player => player.userName.toLowerCase() === currentUsername.toLowerCase()
                );
                if (matchingUserData) {
                  return `Your matching tournament score: ${matchingUserData.score.toLocaleString()} (Rank: #${matchingUserData.rank})`;
                }
                return 'You are not ranked in the matching tournament.';
              })()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ScoreGraph;